import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import * as recruitmentService from '../../services/recruitmentService';

interface InterviewSchedulerProps {
  applicationId: string;
  candidateName: string;
  candidateEmail?: string;
  jobTitle: string;
  onClose: () => void;
  onSuccess: () => void;
}

const InterviewScheduler: React.FC<InterviewSchedulerProps> = ({
  applicationId,
  candidateName,
  candidateEmail,
  jobTitle,
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] = useState({
    interview_type: 'video' as 'video' | 'phone_screen' | 'onsite',
    scheduled_date: '',
    scheduled_time: '',
    duration_minutes: 60,
    location: '',
    video_link: '',
    interviewer_name: '',
    interviewer_email: '',
    notes: '',
    send_calendar_invite: true,
    send_reminder: true,
  });

  const [saving, setSaving] = useState(false);
  const [generatingLink, setGeneratingLink] = useState(false);

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const generateMeetLink = async () => {
    setGeneratingLink(true);
    try {
      // Generate a unique Google Meet style link
      const randomId = Math.random().toString(36).substring(2, 15);
      const meetLink = `https://meet.google.com/${randomId.substring(0, 3)}-${randomId.substring(3, 7)}-${randomId.substring(7, 10)}`;
      
      handleChange('video_link', meetLink);
      toast.success('Video link generated!');
    } catch (error) {
      toast.error('Failed to generate link');
    } finally {
      setGeneratingLink(false);
    }
  };

  const generateZoomLink = async () => {
    setGeneratingLink(true);
    try {
      // Generate a unique Zoom style link
      const randomId = Math.floor(Math.random() * 900000000000) + 100000000000;
      const zoomLink = `https://zoom.us/j/${randomId}`;
      
      handleChange('video_link', zoomLink);
      toast.success('Zoom link generated!');
    } catch (error) {
      toast.error('Failed to generate link');
    } finally {
      setGeneratingLink(false);
    }
  };

  const handleSchedule = async () => {
    try {
      setSaving(true);

      // Validation
      if (!formData.scheduled_date) {
        toast.error('Please select a date');
        return;
      }

      if (!formData.scheduled_time) {
        toast.error('Please select a time');
        return;
      }

      if (formData.interview_type === 'video' && !formData.video_link) {
        toast.error('Please provide a video link');
        return;
      }

      if (formData.interview_type === 'onsite' && !formData.location) {
        toast.error('Please provide a location');
        return;
      }

      // Combine date and time
      const scheduledDateTime = new Date(`${formData.scheduled_date}T${formData.scheduled_time}`).toISOString();

      // Create interview
      await recruitmentService.scheduleInterview({
        application_id: applicationId,
        interview_type: formData.interview_type,
        scheduled_date: scheduledDateTime,
        duration_minutes: formData.duration_minutes,
        location: formData.location || undefined,
        video_link: formData.video_link || undefined,
      });

      toast.success('Interview scheduled successfully!');
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Failed to schedule interview:', error);
      toast.error(error.message || 'Failed to schedule interview');
    } finally {
      setSaving(false);
    }
  };

  // Set default date to tomorrow
  useEffect(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    handleChange('scheduled_date', tomorrow.toISOString().split('T')[0]);
  }, []);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Schedule Interview</h2>
            <p className="text-sm text-gray-500 mt-1">
              {candidateName} • {jobTitle}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Form Content */}
        <div className="p-6 space-y-6">
          {/* Interview Type */}
          <section>
            <label className="block text-sm font-medium text-gray-700 mb-3">Interview Type</label>
            <div className="grid grid-cols-3 gap-3">
              {[
                { value: 'video', label: 'Video Call', icon: '📹' },
                { value: 'phone_screen', label: 'Phone Call', icon: '📞' },
                { value: 'onsite', label: 'In Person', icon: '🏢' },
              ].map((type) => (
                <button
                  key={type.value}
                  type="button"
                  onClick={() => handleChange('interview_type', type.value)}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    formData.interview_type === type.value
                      ? 'border-purple-600 bg-purple-50'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <div className="text-3xl mb-2">{type.icon}</div>
                  <div className="text-sm font-medium text-gray-900">{type.label}</div>
                </button>
              ))}
            </div>
          </section>

          {/* Date & Time */}
          <section>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={formData.scheduled_date}
                  onChange={(e) => handleChange('scheduled_date', e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Time <span className="text-red-500">*</span>
                </label>
                <input
                  type="time"
                  value={formData.scheduled_time}
                  onChange={(e) => handleChange('scheduled_time', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
            </div>
          </section>

          {/* Duration */}
          <section>
            <label className="block text-sm font-medium text-gray-700 mb-2">Duration</label>
            <div className="grid grid-cols-4 gap-2">
              {[30, 45, 60, 90].map((duration) => (
                <button
                  key={duration}
                  type="button"
                  onClick={() => handleChange('duration_minutes', duration)}
                  className={`px-4 py-2 rounded-lg border transition-all ${
                    formData.duration_minutes === duration
                      ? 'border-purple-600 bg-purple-50 text-purple-700 font-medium'
                      : 'border-gray-300 text-gray-700 hover:border-gray-400'
                  }`}
                >
                  {duration} min
                </button>
              ))}
            </div>
          </section>

          {/* Video Link (if video interview) */}
          {formData.interview_type === 'video' && (
            <section>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Video Link <span className="text-red-500">*</span>
              </label>
              <div className="space-y-3">
                <input
                  type="url"
                  value={formData.video_link}
                  onChange={(e) => handleChange('video_link', e.target.value)}
                  placeholder="https://meet.google.com/xxx-xxxx-xxx"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={generateMeetLink}
                    disabled={generatingLink}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium disabled:opacity-50"
                  >
                    {generatingLink ? 'Generating...' : '📹 Generate Google Meet Link'}
                  </button>
                  <button
                    type="button"
                    onClick={generateZoomLink}
                    disabled={generatingLink}
                    className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium disabled:opacity-50"
                  >
                    {generatingLink ? 'Generating...' : '🎥 Generate Zoom Link'}
                  </button>
                </div>
              </div>
            </section>
          )}

          {/* Location (if in-person) */}
          {formData.interview_type === 'onsite' && (
            <section>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Location <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.location}
                onChange={(e) => handleChange('location', e.target.value)}
                placeholder="e.g., Conference Room A, 123 Main St, London"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </section>
          )}

          {/* Interviewer Details */}
          <section>
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Interviewer Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-2">Name</label>
                <input
                  type="text"
                  value={formData.interviewer_name}
                  onChange={(e) => handleChange('interviewer_name', e.target.value)}
                  placeholder="John Smith"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-2">Email</label>
                <input
                  type="email"
                  value={formData.interviewer_email}
                  onChange={(e) => handleChange('interviewer_email', e.target.value)}
                  placeholder="john@company.com"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
            </div>
          </section>

          {/* Notes */}
          <section>
            <label className="block text-sm font-medium text-gray-700 mb-2">Interview Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              placeholder="Any preparation notes, topics to cover, or special instructions..."
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </section>

          {/* Options */}
          <section className="border-t border-gray-200 pt-4">
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="calendar_invite"
                  checked={formData.send_calendar_invite}
                  onChange={(e) => handleChange('send_calendar_invite', e.target.checked)}
                  className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                />
                <label htmlFor="calendar_invite" className="text-sm text-gray-700">
                  Send calendar invite to candidate
                </label>
              </div>
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="send_reminder"
                  checked={formData.send_reminder}
                  onChange={(e) => handleChange('send_reminder', e.target.checked)}
                  className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                />
                <label htmlFor="send_reminder" className="text-sm text-gray-700">
                  Send reminder 24 hours before interview
                </label>
              </div>
            </div>
          </section>

          {/* Candidate Email Notice */}
          {candidateEmail && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <span className="text-2xl">✉️</span>
                <div>
                  <p className="text-sm font-medium text-blue-900">Email will be sent to:</p>
                  <p className="text-sm text-blue-700 mt-1">{candidateEmail}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex items-center justify-between">
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium"
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSchedule}
            className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={saving}
          >
            {saving ? 'Scheduling...' : 'Schedule Interview'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InterviewScheduler;
